import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getGroupById, getActiveMembers } from '../services/groupService';
import { getActivePeriod } from '../services/periodService';
import { getUsersByIds } from '../services/userService';
import { createMatch } from '../services/matchService';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  COLLECTIONS,
  GROUP_MEMBERS_FIELDS,
  RATINGS_FIELDS,
  MATCH_TYPES,
  SET_FIELDS,
  SET_FIELD_VALUES,
  MATCH_TEAM_FIELDS,
} from '../domain/dataModel';

const today = new Date();
today.setHours(0, 0, 0, 0);

function IconArrowLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconShuffle({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M16 3h5v5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 20L21 3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 16v5h-5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 15l6 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 4l5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildKingPairs(order) {
  if (order.length !== 4) return [];
  const [A, B, C, D] = order;
  return [
    { team1: [A, B], team2: [C, D] },
    { team1: [A, C], team2: [B, D] },
    { team1: [A, D], team2: [B, C] },
  ];
}

/** Per ogni campo (ordine 4 giocatori + 3 set): calcola set vinti e game vinti per giocatore, ordina per set decrescente poi game decrescente. Ritorna [{ playerId, setWins, gamesWon }] 1°..4°. */
function computeFieldStandings(order, setScores) {
  if (!order || order.length !== 4 || !setScores || setScores.length < 3) return [];
  const pairs = buildKingPairs(order);
  const stats = { [order[0]]: { setWins: 0, gamesWon: 0 }, [order[1]]: { setWins: 0, gamesWon: 0 }, [order[2]]: { setWins: 0, gamesWon: 0 }, [order[3]]: { setWins: 0, gamesWon: 0 } };
  for (let i = 0; i < 3; i++) {
    const p = pairs[i];
    const s = setScores[i] || { score1: 0, score2: 0 };
    const score1 = Number(s.score1) || 0;
    const score2 = Number(s.score2) || 0;
    const team1Wins = score1 > score2;
    p.team1.forEach((pid) => {
      stats[pid].setWins += team1Wins ? 1 : 0;
      stats[pid].gamesWon += score1;
    });
    p.team2.forEach((pid) => {
      stats[pid].setWins += team1Wins ? 0 : 1;
      stats[pid].gamesWon += score2;
    });
  }
  return order
    .map((playerId) => ({ playerId, setWins: stats[playerId].setWins, gamesWon: stats[playerId].gamesWon }))
    .sort((a, b) => b.setWins - a.setWins || b.gamesWon - a.gamesWon);
}

const TYPE_CONFIG = {
  [MATCH_TYPES.CLASSIC]: {
    label: 'Classica',
    description: 'Partita a coppie fisse. Scegli le due coppie e il numero di set.',
    players: 4,
  },
  [MATCH_TYPES.KING]: {
    label: 'King',
    description: '3 set con rotazione delle coppie. Ordine giocatori casuale (A,B,C,D): Set 1 A+B vs C+D, Set 2 A+C vs B+D, Set 3 A+D vs B+C.',
    players: 4,
  },
  [MATCH_TYPES.SUPER_KING]: {
    label: 'Super King',
    description: '8 giocatori, due campi (Gold e Silver). Serpentina per rating. King per campo poi finali incrociate.',
    players: 8,
  },
};

export default function AddMatch() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [group, setGroup] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);
  const [members, setMembers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [matchType, setMatchType] = useState(null);
  const [playedAt, setPlayedAt] = useState(dayjs());
  const [selectedPlayers, setSelectedPlayers] = useState([]);

  const [classicNumSets, setClassicNumSets] = useState(1);
  const [classicPairA, setClassicPairA] = useState([]);
  const [classicPairB, setClassicPairB] = useState([]);
  const [classicSetScores, setClassicSetScores] = useState([{ score1: 0, score2: 0 }]);

  const [kingOrder, setKingOrder] = useState([]);
  const [kingSetScores, setKingSetScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);

  const [superGoldField, setSuperGoldField] = useState([]);
  const [superSilverField, setSuperSilverField] = useState([]);
  const [superGoldOrder, setSuperGoldOrder] = useState([]);
  const [superSilverOrder, setSuperSilverOrder] = useState([]);
  const [superGoldSetScores, setSuperGoldSetScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);
  const [superSilverSetScores, setSuperSilverSetScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);
  const [superGoldFinalScores, setSuperGoldFinalScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);
  const [superSilverFinalScores, setSuperSilverFinalScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const requiredPlayers = matchType ? TYPE_CONFIG[matchType]?.players ?? 4 : 0;
  useEffect(() => {
    if (!user?.uid) return;
    if (!groupId) {
      setLoading(false);
      setError('ID gruppo mancante. Apri la pagina dal dettaglio del gruppo.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [groupData, active, membersList] = await Promise.all([
          getGroupById(groupId),
          getActivePeriod(groupId),
          getActiveMembers(groupId),
        ]);
        if (cancelled) return;
        if (!groupData) {
          setError('Gruppo non trovato');
          setLoading(false);
          return;
        }
        setGroup(groupData);
        setActivePeriod(active);
        setMembers(membersList);
        if (active) {
          const ratingsSnap = await getDocs(query(collection(db, COLLECTIONS.RATINGS), where(RATINGS_FIELDS.GROUP_ID, '==', groupId), where(RATINGS_FIELDS.PERIOD_ID, '==', active.id), orderBy(RATINGS_FIELDS.POINTS, 'desc')));
          setRatings(ratingsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        setError(err.message || 'Errore di caricamento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, user?.uid]);

  useEffect(() => {
    const ids = [...new Set(members.map((m) => m[GROUP_MEMBERS_FIELDS.USER_ID]).filter(Boolean))];
    if (ids.length === 0) {
      setUsersMap({});
      return;
    }
    getUsersByIds(ids).then((usersData) => {
      const map = {};
      ids.forEach((uid) => {
        const u = usersData[uid];
        map[uid] = u ? (u.displayName || u.email || uid) : uid;
      });
      setUsersMap(map);
    });
  }, [members]);

  const getPlayerId = (m) => (m[GROUP_MEMBERS_FIELDS.IS_GUEST] ? m.id : m[GROUP_MEMBERS_FIELDS.USER_ID]);
  const getDisplayNameForPlayer = (playerId) => {
    const m = members.find((x) => x.id === playerId || x[GROUP_MEMBERS_FIELDS.USER_ID] === playerId);
    if (!m) return String(playerId);
    if (m[GROUP_MEMBERS_FIELDS.IS_GUEST]) return m[GROUP_MEMBERS_FIELDS.GUEST_NICKNAME] || 'Ospite';
    return usersMap[m[GROUP_MEMBERS_FIELDS.USER_ID]] || m[GROUP_MEMBERS_FIELDS.USER_ID];
  };

  const togglePlayer = (playerId) => {
    setSelectedPlayers((prev) => {
      if (prev.includes(playerId)) return prev.filter((id) => id !== playerId);
      if (prev.length >= requiredPlayers) return prev;
      return [...prev, playerId];
    });
  };

  const canAdvanceStep1 = !!matchType && playedAt && playedAt.isValid() && !playedAt.isAfter(dayjs(), 'day');
  const canAdvanceStep2 = selectedPlayers.length === requiredPlayers;

  const superKingGoldStandings = useMemo(
    () => computeFieldStandings(superGoldOrder, superGoldSetScores),
    [superGoldOrder, superGoldSetScores]
  );
  const superKingSilverStandings = useMemo(
    () => computeFieldStandings(superSilverOrder, superSilverSetScores),
    [superSilverOrder, superSilverSetScores]
  );

  useEffect(() => {
    if (matchType !== MATCH_TYPES.KING || selectedPlayers.length !== 4) return;
    if (kingOrder.length === 0) setKingOrder(shuffle(selectedPlayers));
  }, [matchType, selectedPlayers, kingOrder.length]);

  useEffect(() => {
    if (matchType !== MATCH_TYPES.SUPER_KING || selectedPlayers.length !== 8) return;
    if (superGoldField.length === 0 && ratings.length > 0) {
      const byRating = [...selectedPlayers].sort((a, b) => {
        const ra = ratings.find((r) => r[RATINGS_FIELDS.USER_ID] === a)?.[RATINGS_FIELDS.POINTS] ?? 0;
        const rb = ratings.find((r) => r[RATINGS_FIELDS.USER_ID] === b)?.[RATINGS_FIELDS.POINTS] ?? 0;
        return rb - ra;
      });
      setSuperGoldField([byRating[0], byRating[3], byRating[4], byRating[7]]);
      setSuperSilverField([byRating[1], byRating[2], byRating[5], byRating[6]]);
    }
  }, [matchType, selectedPlayers, ratings, superGoldField.length]);

  useEffect(() => {
    if (superGoldField.length === 4 && superGoldOrder.length === 0) setSuperGoldOrder(shuffle(superGoldField));
    if (superSilverField.length === 4 && superSilverOrder.length === 0) setSuperSilverOrder(shuffle(superSilverField));
  }, [superGoldField, superSilverField, superGoldOrder.length, superSilverOrder.length]);

  const regenerateKing = () => setKingOrder(shuffle(selectedPlayers));
  const regenerateSuperGold = () => setSuperGoldOrder(shuffle(superGoldField));
  const regenerateSuperSilver = () => setSuperSilverOrder(shuffle(superSilverField));

  const moveBetweenFields = (fromGold, index) => {
    if (fromGold) {
      const id = superGoldField[index];
      setSuperGoldField((g) => g.filter((_, i) => i !== index));
      setSuperSilverField((s) => [...s, id]);
      setSuperGoldOrder([]);
      setSuperSilverOrder([]);
    } else {
      const id = superSilverField[index];
      setSuperSilverField((s) => s.filter((_, i) => i !== index));
      setSuperGoldField((g) => [...g, id]);
      setSuperGoldOrder([]);
      setSuperSilverOrder([]);
    }
  };

  useEffect(() => {
    if (matchType !== MATCH_TYPES.CLASSIC) return;
    setClassicSetScores((prev) => {
      const next = Array.from({ length: classicNumSets }, (_, i) => prev[i] ?? { score1: 0, score2: 0 });
      return next.length === prev.length ? prev : next;
    });
  }, [classicNumSets, matchType]);

  const buildSetsPayload = () => {
    const sets = [];
    if (matchType === MATCH_TYPES.CLASSIC) {
      for (let i = 0; i < classicNumSets; i++) {
        const s = classicSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: classicPairA[0], [MATCH_TEAM_FIELDS.PLAYER2]: classicPairA[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: classicPairB[0], [MATCH_TEAM_FIELDS.PLAYER2]: classicPairB[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      }
    } else if (matchType === MATCH_TYPES.KING) {
      const pairs = buildKingPairs(kingOrder);
      pairs.forEach((p, i) => {
        const s = kingSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team1[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team1[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team2[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team2[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      });
    } else if (matchType === MATCH_TYPES.SUPER_KING) {
      const goldPairs = buildKingPairs(superGoldOrder);
      goldPairs.forEach((p, i) => {
        const s = superGoldSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.FIELD]: SET_FIELD_VALUES.GOLD_KING,
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team1[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team1[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team2[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team2[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      });
      const silverPairs = buildKingPairs(superSilverOrder);
      silverPairs.forEach((p, i) => {
        const s = superSilverSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.FIELD]: SET_FIELD_VALUES.SILVER_KING,
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team1[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team1[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team2[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team2[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      });
      const gSt = computeFieldStandings(superGoldOrder, superGoldSetScores);
      const sSt = computeFieldStandings(superSilverOrder, superSilverSetScores);
      if (gSt.length >= 4 && sSt.length >= 4) {
        for (let i = 0; i < 3; i++) {
          const s = superGoldFinalScores[i] || { score1: 0, score2: 0 };
          sets.push({
            [SET_FIELDS.FIELD]: SET_FIELD_VALUES.GOLD_FINAL,
            [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[0].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[0].playerId },
            [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[1].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[1].playerId },
            [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
            [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
          });
        }
        for (let i = 0; i < 3; i++) {
          const s = superSilverFinalScores[i] || { score1: 0, score2: 0 };
          sets.push({
            [SET_FIELDS.FIELD]: SET_FIELD_VALUES.SILVER_FINAL,
            [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[2].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[2].playerId },
            [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[3].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[3].playerId },
            [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
            [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
          });
        }
      }
    }
    return sets;
  };

  const removeUndefined = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== undefined)
    );
  };

  const handleSubmit = async () => {
    if (!groupId || !activePeriod || !user?.uid) return;
    setSubmitLoading(true);
    setError(null);
    try {
      const sets = buildSetsPayload();
      const now = new Date();
      const cleanedSets = sets.map((s) =>
        typeof s === 'object' && s !== null ? removeUndefined(s) : s
      );
      const matchPayload = {
        type: matchType,
        sets: cleanedSets,
        playedAt: playedAt.toDate(),
        approvals: [{ userId: user.uid, approvedAt: now }],
      };
      await createMatch(groupId, activePeriod.id, matchPayload, user.uid);
      setSubmitSuccess(true);
      setTimeout(() => navigate(`/groups/${groupId}`), 1500);
    } catch (err) {
      setError(err.message || 'Errore nel salvataggio');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="outline" onClick={() => navigate('/')} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error || 'Gruppo non trovato'}
        </div>
      </div>
    );
  }

  if (!activePeriod) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="outline" onClick={() => navigate(`/groups/${groupId}`)} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <div className="rounded-btn border border-gold/60 bg-gold/15 px-4 py-3 text-sm text-gold">
          Nessun periodo attivo. Impossibile aggiungere una partita.
        </div>
      </div>
    );
  }

  const steps = ['Tipo partita', 'Giocatori', 'Configurazione set', 'Riepilogo e invio'];

  return (
    <div className="space-y-5">
      <div className="mb-2 flex items-center gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(`/groups/${groupId}`)} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <h1 className="font-display text-2xl font-bold text-brand-ice">Aggiungi partita</h1>
      </div>
      {error && (
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}
      {submitSuccess && (
        <div className="rounded-btn border border-win/60 bg-win/15 px-4 py-3 text-sm text-win">
          Partita inviata per approvazione.
        </div>
      )}

      <div className="mb-6 flex items-center gap-1 overflow-x-auto">
        {steps.map((label, idx) => (
          <React.Fragment key={label}>
            <div className="flex shrink-0 items-center gap-1.5">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${idx < activeStep ? 'bg-win text-white' : idx === activeStep ? 'bg-brand-royal text-white' : 'bg-surface-muted text-brand-ice/40'}`}>
                {idx < activeStep ? '✓' : idx + 1}
              </div>
              <span className={`hidden text-xs font-medium sm:block ${idx === activeStep ? 'text-brand-ice' : 'text-brand-ice/40'}`}>{label}</span>
            </div>
            {idx < steps.length - 1 && <div className={`h-px min-w-4 flex-1 ${idx < activeStep ? 'bg-win' : 'bg-surface-muted'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 */}
      {activeStep === 0 && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            {[MATCH_TYPES.CLASSIC, MATCH_TYPES.KING, MATCH_TYPES.SUPER_KING].map((type) => {
              const config = TYPE_CONFIG[type];
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMatchType(type)}
                  className={`rounded-card border p-4 text-left transition-colors ${matchType === type ? 'border-brand-royal bg-brand-royal/10' : 'border-surface-muted bg-surface-card'}`}
                >
                  <Card className="border-0 bg-transparent shadow-none">
                    <CardContent className="space-y-2 p-0">
                      <p className="font-display text-base font-semibold text-brand-ice">{config.label}</p>
                      <p className="text-sm text-brand-ice/70">{config.description}</p>
                      <span className="inline-flex rounded-badge border border-brand-sky/40 bg-brand-sky/15 px-2.5 py-0.5 text-xs font-semibold text-brand-sky">
                        {config.players} giocatori
                      </span>
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
          <div className="space-y-2">
            <Label>Data partita *</Label>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
              <DatePicker
                value={playedAt}
                onChange={(newValue) => setPlayedAt(newValue)}
                maxDate={dayjs()}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: playedAt && playedAt.isAfter(dayjs(), 'day'),
                    helperText: playedAt && playedAt.isAfter(dayjs(), 'day') ? 'La data non può essere nel futuro' : '',
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {activeStep === 1 && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-4 p-4">
            <p className="text-sm text-brand-ice/80">Seleziona esattamente {requiredPlayers} giocatori</p>
            <p className="font-display text-lg font-semibold text-brand-ice">
              {selectedPlayers.length}/{requiredPlayers} selezionati
            </p>
            <div className="space-y-2">
            {members
              .filter((m) => getPlayerId(m))
              .map((m) => {
                const playerId = getPlayerId(m);
                const guest = m[GROUP_MEMBERS_FIELDS.IS_GUEST];
                const selected = selectedPlayers.includes(playerId);
                return (
                  <button
                    key={playerId}
                    type="button"
                    onClick={() => togglePlayer(playerId)}
                    className={`flex w-full items-center justify-between rounded-card border px-3 py-2 text-left ${selected ? 'border-brand-royal/50 bg-brand-royal/15' : 'border-surface-muted bg-surface-card'}`}
                  >
                    <span className="font-medium text-brand-ice">{getDisplayNameForPlayer(playerId)}</span>
                    <span className="flex items-center gap-2">
                      {guest && (
                        <span className="inline-flex rounded-badge border border-gold/40 bg-gold/15 px-2 py-0.5 text-xs font-semibold text-gold">
                          Ospite
                        </span>
                      )}
                      {selected && (
                        <span className="inline-flex rounded-badge border border-brand-royal/40 bg-brand-royal/20 px-2 py-0.5 text-xs font-semibold text-brand-ice">
                          Selezionato
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3 */}
      {activeStep === 2 && matchType === MATCH_TYPES.CLASSIC && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-4 p-4">
            <div className="max-w-xs space-y-2">
              <Label htmlFor="classic-num-sets">Numero di set</Label>
              <Input
                id="classic-num-sets"
                type="number"
                min={1}
                value={classicNumSets}
                onChange={(e) => setClassicNumSets(Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-brand-ice">Coppia A (2 giocatori)</p>
              <div className="mb-2 flex flex-wrap gap-2">
            {selectedPlayers.map((uid) => (
                <button
                  key={uid}
                  type="button"
                  onClick={() => { if (classicPairA.includes(uid)) setClassicPairA((a) => a.filter((id) => id !== uid)); else if (classicPairA.length < 2) setClassicPairA((a) => [...a, uid]); }}
                  className={`rounded-badge border px-3 py-1 text-sm ${classicPairA.includes(uid) ? 'border-brand-royal/50 bg-brand-royal/15 text-brand-ice' : 'border-surface-muted bg-surface-card text-brand-ice/70'}`}
                >
                  {getDisplayNameForPlayer(uid)}
                </button>
            ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-brand-ice">Coppia B (2 giocatori)</p>
              <div className="mb-2 flex flex-wrap gap-2">
            {selectedPlayers.map((uid) => (
                <button
                  key={uid}
                  type="button"
                  onClick={() => { if (classicPairB.includes(uid)) setClassicPairB((b) => b.filter((id) => id !== uid)); else if (classicPairB.length < 2) setClassicPairB((b) => [...b, uid]); }}
                  className={`rounded-badge border px-3 py-1 text-sm ${classicPairB.includes(uid) ? 'border-brand-royal/50 bg-brand-royal/15 text-brand-ice' : 'border-surface-muted bg-surface-card text-brand-ice/70'}`}
                >
                  {getDisplayNameForPlayer(uid)}
                </button>
            ))}
              </div>
            </div>
          {classicPairA.length === 2 && classicPairB.length === 2 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-brand-ice">Punteggi set (Coppia A - Coppia B)</p>
              {classicSetScores.slice(0, classicNumSets).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 text-sm text-brand-ice/80">Set {i + 1}</span>
                    <Input type="number" min={0} value={s.score1} onChange={(e) => setClassicSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                    <span className="text-brand-ice/70">-</span>
                    <Input type="number" min={0} value={s.score2} onChange={(e) => setClassicSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                  </div>
              ))}
              </div>
          )}
          </CardContent>
        </Card>
      )}

      {activeStep === 2 && matchType === MATCH_TYPES.KING && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-3 p-4">
            <Button type="button" variant="outline" onClick={regenerateKing} className="w-fit">
              <IconShuffle className="mr-2 h-4 w-4" />
              Rigenera
            </Button>
          {kingOrder.length === 4 && (
            <>
              {buildKingPairs(kingOrder).map((p, i) => (
                <div key={i} className="space-y-1 rounded-card border border-surface-muted bg-surface px-3 py-2">
                  <p className="text-sm text-brand-ice">
                    Set {i + 1}: {getDisplayNameForPlayer(p.team1[0])} + {getDisplayNameForPlayer(p.team1[1])} vs {getDisplayNameForPlayer(p.team2[0])} + {getDisplayNameForPlayer(p.team2[1])}
                  </p>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} value={kingSetScores[i]?.score1 ?? 0} onChange={(e) => setKingSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                    <span className="text-brand-ice/70">-</span>
                    <Input type="number" min={0} value={kingSetScores[i]?.score2 ?? 0} onChange={(e) => setKingSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                  </div>
                </div>
              ))}
            </>
          )}
          </CardContent>
        </Card>
      )}

      {activeStep === 2 && matchType === MATCH_TYPES.SUPER_KING && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-4 p-4">
            <p className="text-sm text-brand-ice/80">Divisione campi (serpentina per rating)</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <h3 className="font-display text-base font-semibold text-gold">Campo Gold</h3>
              {superGoldField.map((uid, idx) => (
                  <div key={uid} className="flex items-center justify-between rounded-card border border-surface-muted bg-surface px-3 py-2">
                    <span className="rounded-badge border border-gold/30 bg-gold/10 px-2 py-0.5 text-xs font-semibold text-gold">{getDisplayNameForPlayer(uid)}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => moveBetweenFields(true, idx)}>→ Silver</Button>
                  </div>
              ))}
              </div>
              <div className="space-y-2">
                <h3 className="font-display text-base font-semibold text-brand-ice/70">Campo Silver</h3>
              {superSilverField.map((uid, idx) => (
                  <div key={uid} className="flex items-center justify-between rounded-card border border-surface-muted bg-surface px-3 py-2">
                    <span className="rounded-badge border border-brand-sky/30 bg-brand-sky/10 px-2 py-0.5 text-xs font-semibold text-brand-sky">{getDisplayNameForPlayer(uid)}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => moveBetweenFields(false, idx)}>→ Gold</Button>
                  </div>
              ))}
              </div>
            </div>
          {superGoldOrder.length === 4 && superSilverOrder.length === 4 && (
            <>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold text-gold">King Campo Gold</h3>
                    <Button type="button" variant="outline" size="sm" onClick={regenerateSuperGold}>
                      <IconShuffle className="mr-1 h-4 w-4" />
                      Rigenera
                    </Button>
                  </div>
              {buildKingPairs(superGoldOrder).map((p, i) => (
                    <div key={i} className="space-y-1 rounded-card border border-surface-muted bg-surface px-3 py-2">
                      <p className="text-sm text-brand-ice/80">Set {i + 1}: {getDisplayNameForPlayer(p.team1[0])}+{getDisplayNameForPlayer(p.team1[1])} vs {getDisplayNameForPlayer(p.team2[0])}+{getDisplayNameForPlayer(p.team2[1])}</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" min={0} value={superGoldSetScores[i]?.score1 ?? 0} onChange={(e) => setSuperGoldSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                        <span className="text-brand-ice/70">-</span>
                        <Input type="number" min={0} value={superGoldSetScores[i]?.score2 ?? 0} onChange={(e) => setSuperGoldSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                      </div>
                    </div>
              ))}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold text-brand-ice/70">King Campo Silver</h3>
                    <Button type="button" variant="outline" size="sm" onClick={regenerateSuperSilver}>
                      <IconShuffle className="mr-1 h-4 w-4" />
                      Rigenera
                    </Button>
                  </div>
              {buildKingPairs(superSilverOrder).map((p, i) => (
                    <div key={i} className="space-y-1 rounded-card border border-surface-muted bg-surface px-3 py-2">
                      <p className="text-sm text-brand-ice/80">Set {i + 1}: {getDisplayNameForPlayer(p.team1[0])}+{getDisplayNameForPlayer(p.team1[1])} vs {getDisplayNameForPlayer(p.team2[0])}+{getDisplayNameForPlayer(p.team2[1])}</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" min={0} value={superSilverSetScores[i]?.score1 ?? 0} onChange={(e) => setSuperSilverSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                        <span className="text-brand-ice/70">-</span>
                        <Input type="number" min={0} value={superSilverSetScores[i]?.score2 ?? 0} onChange={(e) => setSuperSilverSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                      </div>
                    </div>
              ))}
                </div>
              {/* Substep C — Calcolo automatico finalisti (solo lettura) */}
              {superKingGoldStandings.length === 4 && superKingSilverStandings.length === 4 && (
                  <div className="space-y-2 rounded-card border border-gold/30 bg-gold/10 p-4">
                    <p className="font-display text-base font-semibold text-gold">Calcolo finalisti (automatico)</p>
                    <p className="text-sm text-brand-ice/70">Classifica per set vinti, a parità per game vinti.</p>
                    <p className="text-sm font-semibold text-brand-ice">Classifica Campo Gold</p>
                    <ol className="list-decimal space-y-1 pl-5 text-sm text-brand-ice/80">
                    {superKingGoldStandings.map((row, idx) => (
                        <li key={row.playerId}>
                          {idx + 1}° {getDisplayNameForPlayer(row.playerId)} ({row.setWins} set, {row.gamesWon} game)
                        </li>
                    ))}
                    </ol>
                    <p className="text-sm font-semibold text-brand-ice">Classifica Campo Silver</p>
                    <ol className="list-decimal space-y-1 pl-5 text-sm text-brand-ice/80">
                    {superKingSilverStandings.map((row, idx) => (
                        <li key={row.playerId}>
                          {idx + 1}° {getDisplayNameForPlayer(row.playerId)} ({row.setWins} set, {row.gamesWon} game)
                        </li>
                    ))}
                    </ol>
                    <p className="text-sm font-semibold text-gold">King Gold</p>
                    <p className="text-sm text-brand-ice/80">
                    {getDisplayNameForPlayer(superKingGoldStandings[0].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[0].playerId)} vs {getDisplayNameForPlayer(superKingGoldStandings[1].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[1].playerId)}
                    </p>
                    <p className="text-sm font-semibold text-brand-ice/70">King Silver</p>
                    <p className="text-sm text-brand-ice/80">
                    {getDisplayNameForPlayer(superKingGoldStandings[2].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[2].playerId)} vs {getDisplayNameForPlayer(superKingGoldStandings[3].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[3].playerId)}
                    </p>
                  </div>
              )}
                <p className="text-sm font-semibold text-gold">Substep D — Punteggi finali. Finale King Gold</p>
              {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 text-sm text-brand-ice/80">Set {i + 1}</span>
                    <Input type="number" min={0} value={superGoldFinalScores[i]?.score1 ?? 0} onChange={(e) => setSuperGoldFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                    <span className="text-brand-ice/70">-</span>
                    <Input type="number" min={0} value={superGoldFinalScores[i]?.score2 ?? 0} onChange={(e) => setSuperGoldFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                  </div>
              ))}
                <p className="text-sm font-semibold text-brand-ice/70">Finale King Silver</p>
              {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 text-sm text-brand-ice/80">Set {i + 1}</span>
                    <Input type="number" min={0} value={superSilverFinalScores[i]?.score1 ?? 0} onChange={(e) => setSuperSilverFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                    <span className="text-brand-ice/70">-</span>
                    <Input type="number" min={0} value={superSilverFinalScores[i]?.score2 ?? 0} onChange={(e) => setSuperSilverFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                  </div>
              ))}
            </>
          )}
          </CardContent>
        </Card>
      )}

      {/* STEP 4 */}
      {activeStep === 3 && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-3 p-4">
            <h2 className="font-display text-lg font-semibold text-brand-ice">Riepilogo</h2>
            <p className="text-sm text-brand-ice/80">Tipo partita: {TYPE_CONFIG[matchType]?.label}</p>
            <p className="text-sm text-brand-ice/80">Giocatori: {selectedPlayers.map(getDisplayNameForPlayer).join(', ')}</p>
            <p className="text-sm text-brand-ice/80">Numero set: {buildSetsPayload().length}</p>
            <Button type="button" onClick={handleSubmit} disabled={submitLoading} className="w-full">
            {submitLoading ? 'Invio in corso...' : 'Invia per approvazione'}
          </Button>
          </CardContent>
        </Card>
      )}

      <div className="mt-3 flex items-center justify-between">
        <Button type="button" variant="outline" disabled={activeStep === 0 || submitLoading} onClick={() => setActiveStep((s) => s - 1)}>
          Indietro
        </Button>
        <Button
          type="button"
          disabled={(activeStep === 0 && !canAdvanceStep1) || (activeStep === 1 && !canAdvanceStep2) || submitLoading}
          onClick={() => {
            if (activeStep === steps.length - 1) {
              handleSubmit();
              return;
            }
            setActiveStep((s) => s + 1);
          }}
        >
          {activeStep === steps.length - 1 ? (submitLoading ? 'Invio in corso...' : 'Invia') : 'Avanti'}
        </Button>
      </div>
    </div>
  );
}
